import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { contractorAuth } from '../lib/contractorAuth';
import { startRinging, stopRinging, unlock, blocked, buzz, flashTitle, stopFlashing }
  from '../lib/chime';
import { osNotify, close as closeNotice } from '../lib/osNotify';
import { activeParam, amSilenced } from '../lib/chatStatus';
import './IncomingCall.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Somebody is calling. Answer, or don't.
 *
 * THIS WAS MISSING, and it was the whole second half of the feature. Pressing
 * Call took the caller into a session and told the other side NOTHING: no
 * ring, no banner, no way to answer. The caller sat watching "Waiting for them
 * to join" while the other person, who had no reason to be on the session
 * page, carried on with their day. Gordon's words: "when i call from one side
 * the other side is not showing the call being received or the pc making a
 * sound. I dont even have answer the call button."
 *
 * MOUNTED GLOBALLY, which is the point. A call has to reach somebody wherever
 * they are in the application, not only on the one screen that happens to be
 * polling something. So this sits in the shell and watches a small, cheap
 * endpoint - one indexed query every few seconds, which for a forty-person
 * firm is nothing.
 *
 * NOT THE CHAT POLL. The chat screen's long poll would have been free to
 * extend, and it only runs while the chat screen is open, which is exactly
 * where somebody is not when their phone rings.
 *
 * FOUR WAYS OF SAYING IT, because each can fail alone: a ring, a buzz on a
 * phone, a flashing tab title, and a banner that does not depend on any of
 * them. Browsers refuse audio until the page has had a real user gesture, so
 * the banner is the one that always lands and the rest are there to make
 * somebody look at it.
 */
const POLL_MS = 5000;

/*
 * WHAT TO CALL A RINGING ROW, in the banner and in the desktop notification
 * alike, so the two cannot drift apart.
 *
 * Since group calls, a row may be on a chat group rather than a ticket or the
 * General thread - and it may not be the caller ringing at all, but somebody
 * already in a call adding this person to it. Both are said as they are:
 * "#printers" as the subject, and "Rita is adding you to a call" rather than
 * "Rita is calling you", because the first sentence is the truth when there
 * are already three people talking and the second suggests a quiet one-to-one.
 */
const subjectOf = (call) => {
  if (call.chat_group_id) return call.group_name ? `#${call.group_name}` : 'A group call';
  if (call.ticket_number) return `${call.ticket_number}${call.title ? ` — ${call.title}` : ''}`;
  return 'Not about a ticket';
};
const headlineOf = (call) => (call.invited_by
  ? `${call.invited_by} is adding you to a call`
  : `${call.caller || 'Somebody'} is calling`);

/*
 * WHY THIS TAKES `asContractor` RATHER THAN WORKING THE TOKEN OUT ITSELF.
 *
 * Gordon: "pls just ensure that the ringing is also heard by the admin and
 * users not just the contractor."
 *
 * The server was never the problem. /sessions/ringing rings staff, admins and
 * contractors alike, and e2e-sessions.js proves all three. What was wrong was
 * WHICH IDENTITY THIS COMPONENT ASKED AS.
 *
 * It sent contractorAuth() unconditionally. That helper hands back the
 * contractor token whenever one is in localStorage - which is right for a page
 * only a contractor ever opens, and wrong here, because NOTHING CLEARS
 * contractor_token WHEN STAFF SIGN IN. The only code that removes it is the
 * contractor's own Logout button. So on any browser where a contractor has
 * ever signed in - which is every browser belonging to anybody testing both
 * ends of a call - the staff session went on sending the stale contractor
 * token. attachUser could not resolve it as staff, the route fell through to
 * its contractor branch, and the administrator's own tab polled AS THE
 * CONTRACTOR: told about the contractor's incoming calls, never about its own.
 * The contractor heard the ring. By construction, nobody else could.
 *
 * So the identity is passed in rather than guessed. The shell is the only
 * place that knows whose view this is, and it now says so out loud.
 */
const IncomingCall = ({ enabled = true, asContractor = false }) => {
  const [calls, setCalls] = useState([]);
  const [muzzled, setMuzzled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Sessions this person has already said no to.
   *
   * In memory and nowhere else, deliberately: a dismissal is "not now", not a
   * preference, and a reload is a new decision. Kept in a ref rather than
   * state so dismissing does not have to wait for a render to take effect on
   * the next poll.
   */
  const dismissed = useRef(new Set());
  const ringingFor = useRef(null);
  const answerRef = useRef(null);

  /**
   * Where the person answered FROM, carried into the session so it can bring
   * them back here when the call ends. Gordon asked for that "for all
   * users", and the callee is the one nobody thinks of: the caller pressed
   * Call on a ticket or a chat, but the callee was anywhere at all.
   */
  //
  // THROUGH A REF, read at the moment of answering. As a plain closure the
  // desktop notification's click handler - created once, when the ringing
  // starts - kept the page they were on THEN, so somebody who moved on and
  // answered from the notification was sent back to a page they had already
  // left. It also made sessionPath a new function every render, which the
  // ringing effect could not list as a dependency without re-running on
  // every navigation; Railway builds with CI=true, where that lint warning
  // is an error, and the deploy failed on it.
  const hereRef = useRef('');
  hereRef.current = `${location.pathname}${location.search}`;
  const sessionPath = useCallback(
    (id) => `/sessions/${id}?from=${encodeURIComponent(hereRef.current)}`, []);

  /**
   * Already in a call, or on the way into one.
   *
   * Ringing at somebody who is looking at the session page is ringing at
   * somebody who has already answered - and /tickets/:id/session is the route
   * CallButton navigates to, so it has to count as well.
   */
  const inACall = /^\/sessions\//.test(location.pathname)
    || /\/session$/.test(location.pathname);

  const look = useCallback(async () => {
    try {
      // undefined for staff, so the request goes through the axios
      // interceptor and picks up the Entra token - instead of carrying a
      // contractor token that happens to still be in this browser.
      /*
       * `active` is this browser saying somebody is actually at the keyboard
       * rather than that a tab is open. THIS POLL IS NOW THE HEARTBEAT for
       * the whole application - it is the only one every page runs - so the
       * online dot finally means "is using Jarvis" rather than "has the chat
       * screen open", which is all it ever meant before. The server stamps it
       * through lib/chat-status.js; see routes/sessions.js.
       */
      const res = await axios.get(`${API_BASE}/sessions/ringing`, {
        ...(asContractor ? contractorAuth() : {}),
        params: activeParam()
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      /*
       * Do not disturb is enforced on the SERVER - /sessions/ringing answers
       * an empty list - because it has to hold for every tab and every device
       * at once, including a phone no React state here knows about. This is
       * the belt to that braces: a status set in another tab a moment ago
       * silences this one without waiting for its own poll to catch up.
       */
      setCalls(amSilenced() ? []
        : rows.filter((c) => !dismissed.current.has(c.id)));
    } catch {
      // Silent on purpose. This runs every few seconds, and a banner that
      // appears because the network hiccupped is worse than one that is
      // briefly late.
      setCalls([]);
    }
    // asContractor is a dependency, not a constant: an administrator opening
    // the portal preview and coming back out changes who should be rung, and
    // a callback closed over the old answer would keep asking as the wrong
    // person for the rest of the session.
  }, [asContractor]);

  useEffect(() => {
    if (!enabled || inACall) { setCalls([]); return undefined; }
    look();
    const id = setInterval(look, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, inACall, look]);

  /** Let the page make a noise, on the first interaction of any kind. */
  useEffect(() => {
    const open = () => { unlock(); setMuzzled(blocked()); };
    for (const type of ['pointerdown', 'keydown', 'touchstart']) {
      window.addEventListener(type, open, { once: true, passive: true });
    }
    return () => {
      for (const type of ['pointerdown', 'keydown', 'touchstart']) {
        window.removeEventListener(type, open);
      }
    };
  }, []);

  /** Ring for a NEW call, and stop when there is nothing left to ring about. */
  useEffect(() => {
    const first = calls[0];
    if (!first) {
      if (ringingFor.current) {
        stopRinging();
        stopFlashing();
        ringingFor.current = null;
      }
      return;
    }
    if (ringingFor.current === first.id) return;
    ringingFor.current = first.id;
    // Longer and more insistent than the ask-to-share ring: this one is
    // somebody waiting on the line rather than a question that can sit.
    const audible = startRinging({ times: 12, everyMs: 2200 });
    if (!audible) setMuzzled(true);
    buzz([200, 120, 200, 120, 200]);
    if (document.hidden) {
      flashTitle(headlineOf(first));
      // The only one of these that appears when Jarvis is behind another
      // window entirely. A web page cannot raise its own window above other
      // applications - nothing in a browser can - and this is the nearest
      // thing there is. Does nothing unless somebody has allowed it.
      osNotify({
        title: headlineOf(first),
        body: subjectOf(first),
        tag: `call-${first.id}`,
        kind: 'calls',
        onClick: () => { window.location.assign(sessionPath(first.id)); }
      });
    }
    // sessionPath is stable (useCallback with no inputs, reading a ref), so
    // listing it changes nothing about when this runs.
  }, [calls, sessionPath]);

  useEffect(() => {
    const seen = () => { if (!document.hidden) stopFlashing(); };
    document.addEventListener('visibilitychange', seen);
    return () => document.removeEventListener('visibilitychange', seen);
  }, []);

  // Nothing left ringing after this unmounts, and nothing left flashing in a
  // tab title somebody then wonders about.
  useEffect(() => () => { stopRinging(); stopFlashing(); closeNotice(); }, []);

  /**
   * Focus the Answer button, and let Escape decline.
   *
   * Escape declines rather than doing nothing: a modal with no keyboard way
   * out is an accessibility problem, and declining is recoverable - they can
   * ring again. Answer is focused so Enter answers, because that is what
   * somebody does with a ringing phone nine times out of ten.
   */
  useEffect(() => {
    if (calls.length === 0) return undefined;
    answerRef.current?.focus();
    const key = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(calls[0]); }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls]);

  const answer = (call) => {
    stopRinging();
    stopFlashing();
    closeNotice();
    ringingFor.current = null;
    setCalls([]);
    navigate(sessionPath(call.id));
  };

  /**
   * "Not now" - and telling the person who rang.
   *
   * Gordon: "if any user declines a call - the call sender popup should
   * close." This used to be local and only local: the id went into a Set in
   * this component and nothing else happened, so the caller sat watching
   * "Waiting for them to join" until the ten-minute ring window ran out, and
   * the same call rang again on a reload or on another device.
   *
   * The local part still happens FIRST and unconditionally. A phone that
   * keeps ringing because the network was slow is the one failure nobody
   * forgives, so the ringing stops the instant the button is pressed and the
   * server is told afterwards. If that request never lands, the only thing
   * lost is the caller learning why - which is exactly where this started,
   * so it is no worse than before and never worse than a stuck ring.
   */
  const dismiss = (call) => {
    dismissed.current.add(call.id);
    stopRinging();
    stopFlashing();
    closeNotice();
    ringingFor.current = null;
    setCalls((prev) => prev.filter((c) => c.id !== call.id));
    axios.post(`${API_BASE}/sessions/${call.id}/decline`, {},
      asContractor ? contractorAuth() : undefined).catch(() => {});
  };

  if (!enabled || inACall || calls.length === 0) return null;
  const call = calls[0];

  return (
    <div className="ic-scrim" role="presentation">
      <div
        className="ic-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ic-head"
        aria-describedby="ic-about"
      >
        <span className="ic-pulse" aria-hidden="true" />

        <strong id="ic-head" className="ic-head">
          {call.invited_by || call.caller || 'Somebody'}
          {!call.invited_by && call.caller_role === 'fixer' ? ' (IT)' : ''}
        </strong>
        <span className="ic-calling">
          {call.invited_by ? 'is adding you to a call' : 'is calling you'}
        </span>

        <span id="ic-about" className="ic-about">
          {/* A call from the General thread has no ticket, which is said
              rather than left as a blank "About". A group call names the
              group, the way the chat's own list does. */}
          {call.chat_group_id
            ? <span className="ic-group">{subjectOf(call)}</span>
            : call.ticket_number
              ? <>About <span className="ic-number">{call.ticket_number}</span>
                  {call.title ? ` — ${call.title}` : ''}</>
              : 'Not about a ticket'}
        </span>

        {muzzled && (
          <span className="ic-quiet">
            Your browser is holding sounds back until you interact with the page.
          </span>
        )}

        <div className="ic-buttons">
          {/* Focused on arrival, so Enter answers. The commonest thing
              somebody does with a ringing phone should take no aim. */}
          <button type="button" className="ic-answer" ref={answerRef}
            onClick={() => answer(call)}>
            Answer
          </button>
          <button type="button" className="ic-decline" onClick={() => dismiss(call)}>
            Not now
          </button>
        </div>

        {calls.length > 1 && (
          <span className="ic-more">and {calls.length - 1} more waiting</span>
        )}
      </div>
    </div>
  );
};

export default IncomingCall;
